import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { convertToXml, loadECMRFromLocalStorage } from '../services/eCMRService';
import { ECMR } from '../types/eCMR';
import { validateECMR } from '../services/schemaValidator';

/**
 * Componente para validar un documento eCMR contra el estándar UN/CEFACT
 */
const ValidateECMR: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ecmr, setEcmr] = useState<ECMR | null>(null);
  const [xmlContent, setXmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [validationResults, setValidationResults] = useState<{ valid: boolean, errors?: string[] } | null>(null);
  const [validating, setValidating] = useState<boolean>(false);

  useEffect(() => {
    if (!id) {
      setError('ID de e-CMR no proporcionado');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const loadedEcmr = loadECMRFromLocalStorage(id);
        if (!loadedEcmr) {
          setError('e-CMR no encontrado');
          setLoading(false);
          return;
        }

        setEcmr(loadedEcmr);
        
        // Convertir a XML para validación
        const xml = await convertToXml(loadedEcmr);
        setXmlContent(xml);
        
        setLoading(false);
      } catch (err) {
        setError(`Error al cargar e-CMR: ${(err as Error).message}`);
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleValidate = async () => {
    if (!xmlContent) return;
    
    setValidating(true);
    
    try {
      const result = await validateECMR(xmlContent);
      setValidationResults(result);
    } catch (error) {
      setError(`Error al validar: ${(error as Error).message}`);
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center">
          <p className="text-lg">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Volver a la lista
        </button>
      </div>
    );
  }

  if (!ecmr) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
          <p>No se encontró el documento e-CMR</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Volver a la lista
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          Validar e-CMR: {ecmr.ExchangedDocument?.ID || 'Sin título'}
        </h1>
        
        <div className="flex space-x-2">
          <button
            onClick={handleValidate}
            disabled={validating}
            className={`bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
              validating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {validating ? 'Validando...' : 'Validar contra estándar UN/CEFACT'}
          </button>
          
          <Link
            to={`/view/${id}`}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Ver documento
          </Link>
          
          <button
            onClick={() => navigate('/')}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
          >
            Volver
          </button>
        </div>
      </div>
      
      {validationResults && (
        <div className={`mb-6 p-4 rounded ${validationResults.valid ? 'bg-green-100 border-l-4 border-green-500 text-green-700' : 'bg-red-100 border-l-4 border-red-500 text-red-700'}`}>
          <h2 className="text-xl font-semibold mb-2">
            {validationResults.valid 
              ? 'El documento cumple con el estándar UN/CEFACT' 
              : 'El documento no cumple completamente con el estándar UN/CEFACT'}
          </h2>
          
          {validationResults.errors && validationResults.errors.length > 0 && (
            <div className="mt-2">
              <h3 className="font-bold">Problemas encontrados:</h3>
              <ul className="list-disc pl-5 mt-2">
                {validationResults.errors.map((error, index) => (
                  <li key={index} className="text-sm mt-1">{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      <div className="bg-white shadow-md rounded p-6">
        <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Información del Estándar</h2>
        <div className="mb-4">
          <p>
            Los documentos e-CMR deben cumplir con el estándar UN/CEFACT específico para cartas de porte electrónicas.
            Esta validación comprueba la conformidad con:
          </p>
          <ul className="list-disc pl-5 mt-2">
            <li>Estructura básica del documento según el esquema XSD</li>
            <li>Presencia de campos obligatorios</li>
            <li>Formato correcto de los datos (fechas, códigos, etc.)</li>
            <li>Validez de los códigos utilizados según las listas de códigos oficiales</li>
          </ul>
        </div>
        
        <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Esquemas XSD de referencia</h2>
        <div className="mb-4">
          <ul className="list-disc pl-5">
            <li>eCMR_100pD24A.xsd - Esquema principal</li>
            <li>eCMR_100pD24A_urn_un_unece_uncefact_data_standard_ReusableAggregateBusinessInformationEntity_100.xsd</li>
            <li>eCMR_100pD24A_urn_un_unece_uncefact_data_standard_QualifiedDataType_100.xsd</li>
            <li>eCMR_100pD24A_urn_un_unece_uncefact_data_standard_UnqualifiedDataType_100.xsd</li>
            <li>eCMR_100pD24A_urn_un_unece_uncefact_codelist_standard_UNECE_PartyRoleCode_D23A.xsd</li>
            <li>eCMR_100pD24A_urn_un_unece_uncefact_codelist_standard_UNECE_TransportModeCode_2.xsd</li>
            <li>Y otros esquemas de códigos relacionados</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ValidateECMR; 